"use client"

import { useEffect, useState } from "react"

import type { RunRowLike } from "@/types/RunRow"

import {
  isSimulationListenable,
  isSimulationLive,
} from "../utils/isSimulationLive"

interface SimulationRoomSelection {
  /** The simulation the UI should show a room for, or null for no dock at all. */
  selectedId: string | null
  /** Every simulation currently open to listeners, in row order. */
  listenableIds: string[]
  /** Whether the selected simulation is still running. */
  isSelectedLive: boolean
  /** Whether the user is currently listening to the selected simulation. */
  isEngaged: boolean
  /** Manual pick. An active listener stays active on the new simulation. */
  select: (id: string) => void
  /** Report that the user started listening. */
  handleStart: () => void
  /** Report that the user stopped listening. */
  handleStop: () => void
}

/**
 * Which simulation the live-audio UI is pointed at, given a run's rows.
 *
 * This is the policy half of the dock, kept out of the presentation so the
 * chrome can move (page header, side panel, detail page) without carrying the
 * state machine along. Exactly one selection exists per hook instance, which
 * is what enforces the one-RTC-connection-at-a-time rule: the UI keys its
 * room mount on `selectedId`, so a switch tears the old connection down
 * before the next one is requested.
 *
 * Audio never starts on its own. Selection moves around (auto-pick, a
 * finished simulation being replaced, a manual switch). A manual switch keeps
 * an active listener engaged while the new room is mounted.
 */
export function useSimulationRoomSelection(
  runRows: RunRowLike[],
): SimulationRoomSelection {
  const listenable = runRows.filter(isSimulationListenable)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [engaged, setEngaged] = useState(false)

  // Point at a running simulation as soon as one is available. The selection
  // always tracks a listenable row when one exists — including replacing one
  // that finished before the user ever pressed Listen.
  useEffect(() => {
    if (selectedId === null && listenable.length > 0) {
      setSelectedId(listenable[0].simulation_output_id)
      return
    }
    if (listenable.some((row) => row.simulation_output_id === selectedId)) {
      return
    }
    if (listenable.length > 0) {
      // An automatic replacement is not a user gesture, so the new room
      // starts muted even if the previous room was being listened to.
      setEngaged(false)
      setSelectedId(listenable[0].simulation_output_id)
      return
    }
    // Nothing live is left to replace it with. An engaged listener keeps
    // their dock — that's the completed-session flow (a "Close" button, not
    // a vanishing panel) — but an unengaged auto-pick that finished before
    // Listen was ever pressed has nothing left to show.
    if (!engaged) {
      setSelectedId(null)
    }
  }, [engaged, listenable, selectedId])

  return {
    selectedId,
    isEngaged: engaged,
    listenableIds: listenable.map((row) => row.simulation_output_id),
    isSelectedLive: runRows.some(
      (row) => row.simulation_output_id === selectedId && isSimulationLive(row),
    ),
    select: (id: string) => {
      setSelectedId(id)
    },
    handleStart: () => setEngaged(true),
    handleStop: () => setEngaged(false),
  }
}
